import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiUploadCloud } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { VIDEO_GET, VIDEO_UPDATE, VIDEO_UPLOAD, VIDEO_THUMBNAIL_UPLOAD, PLAYLIST_LIST, CATEGORY_LIST } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import MultiSelect from '../MultiSelect';
import './Page.css';
import './CreateCategory.css';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const AGE_BUCKET_OPTIONS = [
  { value: '0-3', label: '0-3' },
  { value: '3-6', label: '3-6' },
  { value: '6+', label: '6+' },
];

const CONTENT_TYPE_OPTIONS = [
  { value: 'Licensed', label: 'Licensed' },
  { value: 'Original', label: 'Original' },
];

function normalizeIds(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (v != null && v !== '') return [String(v)];
  return [];
}

function EditVideo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [title, setTitle] = useState('');
  const [channel_name, setChannel_name] = useState('');
  const [feed_key, setFeed_key] = useState('');
  const [status, setStatus] = useState('active');
  const [thumbnail_url, setThumbnail_url] = useState('');
  const [video_url, setVideo_url] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [age_buckets, setAge_buckets] = useState([]);
  const [content_types, setContent_types] = useState([]);
  const [playlist_ids, setPlaylist_ids] = useState([]);
  const [category_ids, setCategory_ids] = useState([]);
  const [playlistOptions, setPlaylistOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError('Invalid video.');
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(VIDEO_GET.pathFor(id)), { method: VIDEO_GET.method, headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Video not found.' : `Request failed (${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json.data || json;
        setVideo(data);
        setTitle(data.title ?? '');
        setChannel_name(data.channel_name ?? '');
        setFeed_key(data.feed_key ?? '');
        setStatus((data.status || 'active').toLowerCase());
        setThumbnail_url(data.thumbnail_url ?? '');
        setVideo_url(data.video_url ?? '');
        setAge_buckets(Array.isArray(data.age_buckets) ? data.age_buckets.map((v) => (v === '<3' ? '0-3' : v)) : []);
        setContent_types(Array.isArray(data.content_types) ? data.content_types : []);
        setPlaylist_ids(normalizeIds(data.playlist_ids ?? data.playlist_id));
        setCategory_ids(normalizeIds(data.category_ids ?? data.category_id));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Unable to load video. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      fetch(apiUrl(PLAYLIST_LIST.path), { headers }).then((r) => r.json()).catch(() => ({ data: [] })),
      fetch(apiUrl(CATEGORY_LIST.path), { headers }).then((r) => r.json()).catch(() => ({ data: [] })),
    ]).then(([plRes, catRes]) => {
      if (cancelled) return;
      setPlaylistOptions((plRes.data || []).map((p) => ({ value: p.id || p.title, label: p.title || p.id || '—' })));
      setCategoryOptions((catRes.data || []).map((c) => ({ value: c.id, label: c.name || c.id || '—' })));
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    return () => {
      if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
    };
  }, [thumbnailPreview]);

  const handleVideoFileChange = async (e) => {
    const file = e.target.files?.[0];
    setVideoFile(file || null);
    setError('');
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file.');
      setVideoFile(null);
      return;
    }

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(VIDEO_UPLOAD.path), {
        method: VIDEO_UPLOAD.method,
        headers,
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status, { default: 'Video upload failed. Please try again.' }));
        setUploadingVideo(false);
        return;
      }
      const uploadedUrl = data.data?.video_url || data.video_url || '';
      if (!uploadedUrl) {
        setError('Video uploaded but no video URL returned.');
        setUploadingVideo(false);
        return;
      }
      setVideo_url(uploadedUrl);
    } catch (err) {
      setError(err.message || 'Unable to upload video. Please try again.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files?.[0];
    setError('');

    if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) {
      URL.revokeObjectURL(thumbnailPreview);
      setThumbnailPreview(null);
    }

    if (!file) {
      setThumbnailFile(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file for thumbnail.');
      setThumbnailFile(null);
      return;
    }

    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));

    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(VIDEO_THUMBNAIL_UPLOAD.path), {
        method: VIDEO_THUMBNAIL_UPLOAD.method,
        headers,
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status, { default: 'Thumbnail upload failed. Please try again.' }));
        setUploadingThumbnail(false);
        return;
      }
      const uploadedUrl = data.data?.thumbnail_url || data.thumbnail_url || '';
      if (!uploadedUrl) {
        setError('Thumbnail uploaded but no thumbnail URL returned.');
        setUploadingThumbnail(false);
        return;
      }
      setThumbnail_url(uploadedUrl);
    } catch (err) {
      setError(err.message || 'Unable to upload thumbnail. Please try again.');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required.');
      return;
    }
    const finalVideoUrl = (video_url || '').trim();
    if (!finalVideoUrl) return setError('Please upload a video.');
    const finalThumbUrl = (thumbnail_url || '').trim();
    if (!finalThumbUrl) return setError('Please upload a thumbnail image.');

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        title: trimmedTitle,
        channel_name: channel_name.trim() || undefined,
        feed_key: feed_key.trim() || undefined,
        status,
        thumbnail_url: finalThumbUrl,
        video_url: finalVideoUrl,
        age_buckets: age_buckets.length ? age_buckets : undefined,
        content_types: content_types.length ? content_types : undefined,
        playlist_ids: playlist_ids.length ? playlist_ids : undefined,
        category_ids: category_ids.length ? category_ids : undefined,
      };
      const res = await fetch(apiUrl(VIDEO_UPDATE.pathFor(id)), {
        method: VIDEO_UPDATE.method,
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setIsSubmitting(false);
        return;
      }
      navigate('/dashboard/cms/video');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page create-category-page">
        <p className="create-category-loading">Loading video…</p>
      </div>
    );
  }

  if (error && !video) {
    return (
      <div className="page create-category-page">
        <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/video')}>
          <FiArrowLeft className="back-icon" aria-hidden />
          Back to Video
        </button>
        <p className="create-category-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="page create-category-page">
      <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/video')}>
        <FiArrowLeft className="back-icon" aria-hidden />
        Back to Video
      </button>

      <div className="create-category-header">
        <h3>Edit Video</h3>
        <p className="create-category-subtitle">
          Update video details. You can replace the video by uploading a new file or change the URL.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="create-category-form">
        <div className="form-page-layout">
          <div className="form-section">
            <h4 className="form-section-title">Video</h4>
            <div className="form-row-single">
              <label>Replace video (optional)</label>
              <div className="thumbnail-upload-area">
                <input
                  type="file"
                  id="edit-video-upload"
                  accept="video/*"
                  onChange={handleVideoFileChange}
                  disabled={isSubmitting || uploadingVideo || uploadingThumbnail}
                  className="thumbnail-input"
                />
                <label htmlFor="edit-video-upload" className="thumbnail-upload-label">
                  <FiUploadCloud className="upload-icon" aria-hidden />
                  <span>{videoFile ? videoFile.name : 'Choose video file to replace'}</span>
                </label>
              </div>
              <p className="form-hint">{uploadingVideo ? 'Uploading video…' : 'If you upload a file, it replaces the current video.'}</p>
            </div>
            <div className="form-row-single">
              <label htmlFor="edit-video-url">Video (uploaded)</label>
              <input
                id="edit-video-url"
                type="text"
                value={video_url}
                readOnly
                disabled
                placeholder="Upload video to update URL"
              />
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">Details</h4>
            <div className="form-grid-2">
              <div className="form-row-single">
                <label htmlFor="edit-video-title">Title <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="edit-video-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Lets_Learn_About_Vehicles"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-video-status">Status</label>
                <select id="edit-video-status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isSubmitting}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-video-channel">Channel name</label>
                <input
                  id="edit-video-channel"
                  type="text"
                  value={channel_name}
                  onChange={(e) => setChannel_name(e.target.value)}
                  placeholder="e.g. kidzoki"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-video-feed">Feed key</label>
                <input
                  id="edit-video-feed"
                  type="text"
                  value={feed_key}
                  onChange={(e) => setFeed_key(e.target.value)}
                  placeholder="e.g. FEED"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">Thumbnail</h4>
            <div className="form-thumbnail-with-preview">
              <div className="thumbnail-upload-area">
                <input
                  type="file"
                  id="edit-video-thumbnail-upload"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  disabled={isSubmitting || uploadingVideo || uploadingThumbnail}
                  className="thumbnail-input"
                />
                <label htmlFor="edit-video-thumbnail-upload" className="thumbnail-upload-label">
                  <FiUploadCloud className="upload-icon" aria-hidden />
                  <span>{thumbnailFile ? thumbnailFile.name : 'Choose thumbnail image to replace'}</span>
                </label>
                <p className="form-hint">{uploadingThumbnail ? 'Uploading thumbnail…' : 'Upload to replace the current thumbnail.'}</p>
              </div>
              {thumbnailPreview ? (
                <div className="thumbnail-preview-wrap">
                  <img src={thumbnailPreview} alt="New thumbnail preview" className="thumbnail-preview-img" />
                </div>
              ) : thumbnail_url ? (
                <div className="thumbnail-preview-wrap">
                  <img src={thumbnail_url} alt="Current thumbnail" className="thumbnail-preview-img" />
                </div>
              ) : null}
            </div>
            <div className="form-row-single" style={{ marginTop: 16 }}>
              <label htmlFor="edit-video-thumb-url">Thumbnail (uploaded)</label>
              <input id="edit-video-thumb-url" type="text" value={thumbnail_url} readOnly disabled placeholder="Upload thumbnail to update URL" />
            </div>
          </div>

          <div className="form-section form-section-full">
            <h4 className="form-section-title">Mapping (optional)</h4>
            <div className="form-grid-3">
              <div className="form-row-single">
                <label>Age buckets</label>
                <MultiSelect
                  options={AGE_BUCKET_OPTIONS}
                  value={age_buckets}
                  onChange={setAge_buckets}
                  placeholder="Select age groups"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single">
                <label>Content types</label>
                <MultiSelect
                  options={CONTENT_TYPE_OPTIONS}
                  value={content_types}
                  onChange={setContent_types}
                  placeholder="Select content types"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single" />
            </div>
            <div className="form-grid-2" style={{ marginTop: 20 }}>
              <div className="form-row-single">
                <label>Categories</label>
                <MultiSelect
                  options={playlistOptions}
                  value={playlist_ids}
                  onChange={setPlaylist_ids}
                  placeholder="Select categories (multiple)"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single">
                <label>Parent categories</label>
                <MultiSelect
                  options={categoryOptions}
                  value={category_ids}
                  onChange={setCategory_ids}
                  placeholder="Select parent categories (multiple)"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>

        {error && <div className="create-category-error" role="alert">{error}</div>}

        <div className="form-actions form-actions-full">
          <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard/cms/video')} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={isSubmitting || uploadingVideo || uploadingThumbnail}>
            {uploadingVideo || uploadingThumbnail ? 'Uploading…' : isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditVideo;
